import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { GetEmployeesbyBranchID, getSubscribeBrandInfo } from "@api/api";
import {
  getBranchAndPermissionByToken,
  getRolePermissionsByBrand,
} from "@api/api_permission";
import { resolveCashierBranchEmployeeId } from "@helpers/resolveCashierBranchEmployeeId";
import {
  buildMyAccountPermissionCheckboxTrees,
  buildPermissionCheckboxTreesFromNested,
  formatPermissionRoleDisplayName,
  resolveUserDisplayName,
  resolveUserPermissionRoleName,
  findPermissionRoleNameByPermissions,
  resolveBranchRoleFromBranchPermissionEntry,
  resolveBranchRoleFromEmployeeRecord,
  resolvePermissionTemplateRoleRow,
  buildMyAccountBranchPermissionRows,
  buildFullBrandBranchList,
  normalizeSubscribeBranchesForCatalog,
  mergeAssignedBranchRoles,
  mergeBranchCatalogEntries,
  assignedBranchRolesFromPermissionEntries,
  MY_ACCOUNT_NO_BRANCH_ROLE_LABEL,
} from "@helpers/permissionCheckboxTree";
import { MODAL_SLIDE_CLOSE_MS } from "@constants/modalAnimation";
import {
  loadMyAccountPermissionsSnapshot,
  saveMyAccountPermissionsSnapshot,
} from "@helpers/myAccountPermissionsSnapshot";
import { IoMdClose } from "react-icons/io";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import ReadOnlyPermissionCheckboxTree from "./ReadOnlyPermissionCheckboxTree";
import SelectDropdownEmptyHint from "../SelectDropdownEmptyHint";
import { CheckoutDetailsPerfectScrollbar } from "@components/CheckoutDetailsScrollbar";
import "@components/CheckoutDetailsScrollbar/checkout-details-scrollbar.scss";

function readStoredEmployeeBranchRoles() {
  try {
    const raw = localStorage.getItem("eternalEmployeeInfo");
    if (!raw) return [];
    const info = JSON.parse(raw);
    return info?.branch_roles || info?.branchRoles || [];
  } catch(err) {
    console.log("Error reading stored employee branch roles", err);
    return [];
  }
}

const PopMyPermissions = ({ open, onClose }) => {
  const token = useSelector((state) => state.user.user.token);
  const branchId = useSelector((state) => state.current.branchId);
  const branchInfo = useSelector((state) => state.current.branchInfo);
  const permissions = useSelector((state) => state.user.user.permissions);
  const employeeInfo = useSelector((state) => state.user.user.employeeInfo);
  const branchStatus = useSelector((state) => state.user.user.branchStatus);
  const brandInfo = useSelector((state) => state.user.user.brandInfo);
  const brandId = useSelector((state) => state.user.user.brandId);
  const subscribeBranchesRedux = useSelector(
    (state) => state.subscribe.subscribe.branches
  );
  const [closing, setClosing] = useState(false);
  const [permissionRoleName, setPermissionRoleName] = useState("");
  const [branchPermissionRows, setBranchPermissionRows] = useState([]);
  const [permissionTrees, setPermissionTrees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showBranchListPopup, setShowBranchListPopup] = useState(false);
  const permissionTreePsRef = useRef(null);

  const displayName = useMemo(
    () => resolveUserDisplayName({ employeeInfo, branchStatus, brandInfo }),
    [employeeInfo, branchStatus, brandInfo]
  );

  const brandBranches = useMemo(
    () => (Array.isArray(brandInfo?.branches) ? brandInfo.branches : []),
    [brandInfo?.branches]
  );

  useEffect(() => {
    if (!open) {
      setPermissionRoleName("");
      setBranchPermissionRows([]);
      setPermissionTrees([]);
      setIsLoading(false);
      setShowBranchListPopup(false);
      return undefined;
    }

    let cancelled = false;

    const applyMyPermissionsView = ({
      roles,
      branchEntries,
      selfRecord,
      branchPermissionEntry,
      employeeRecordBranchRole,
      subscribeBrandBranches = [],
    }) => {
      const snapshot = loadMyAccountPermissionsSnapshot();
      const subscribeCatalog = normalizeSubscribeBranchesForCatalog(
        subscribeBrandBranches
      );
      const extraBrandBranches = buildFullBrandBranchList(
        [],
        [],
        [],
        [
          ...(snapshot?.brandBranches || []),
          ...subscribeCatalog,
        ]
      );
      const mergedEntries = mergeBranchCatalogEntries(
        snapshot?.branchEntries,
        branchEntries
      );
      const mergedAssigned = mergeAssignedBranchRoles(
        snapshot?.assignedBranchRoles,
        readStoredEmployeeBranchRoles(),
        employeeInfo?.branch_roles,
        employeeInfo?.branchRoles,
        selfRecord?.branch_roles,
        selfRecord?.branchRoles,
        assignedBranchRolesFromPermissionEntries(branchEntries)
      );
      const rows = buildMyAccountBranchPermissionRows({
        branchId,
        currentBranchInfo: branchInfo,
        employeeInfo,
        assignedBranchRoles: mergedAssigned,
        brandBranches,
        extraBrandBranches,
        branchEntries: mergedEntries,
        permissionRoles: roles,
        branchStatus,
      });

      const templateRoleRow = resolvePermissionTemplateRoleRow(roles, permissions, {
        roleId:
          employeeRecordBranchRole?.roleId ?? branchPermissionEntry?.roleId,
      });

      const currentRow = rows.find((row) => row.isCurrent);
      const roleFromRow =
        currentRow?.hasPermission &&
        currentRow.roleName &&
        currentRow.roleName !== MY_ACCOUNT_NO_BRANCH_ROLE_LABEL
          ? currentRow.roleName
          : null;

      const fromSources = resolveUserPermissionRoleName({
        token,
        employeeInfo,
        branchId,
        permissionRoles: roles,
        branchPermissionEntry,
        employeeRecordBranchRole,
      });

      const matched = findPermissionRoleNameByPermissions(roles, permissions);

      const displayRole =
        roleFromRow ||
        fromSources ||
        matched ||
        formatPermissionRoleDisplayName(templateRoleRow?.role?.roleName) ||
        (currentRow?.roleName && currentRow.roleName !== MY_ACCOUNT_NO_BRANCH_ROLE_LABEL
          ? currentRow.roleName
          : null) ||
        "—";

      if (mergedAssigned.length > 0 || mergedEntries.length > 0) {
        saveMyAccountPermissionsSnapshot({
          assignedBranchRoles: mergedAssigned,
          branchEntries: mergedEntries,
          brandBranches: buildFullBrandBranchList(
            brandBranches,
            mergedEntries,
            mergedAssigned,
            extraBrandBranches
          ),
        });
      }

      return {
        rows,
        displayRole,
        trees: buildMyAccountPermissionCheckboxTrees(
          templateRoleRow?.permissions,
          permissions
        ),
      };
    };

    const loadMyPermissions = async () => {
      setIsLoading(true);
      try {
        if (!token || !branchId) {
          const view = applyMyPermissionsView({
            roles: [],
            branchEntries: [],
            selfRecord: null,
            branchPermissionEntry: null,
            employeeRecordBranchRole: null,
          });
          if (!cancelled) {
            setPermissionRoleName(view.displayRole);
            setBranchPermissionRows(view.rows);
            setPermissionTrees(
              buildPermissionCheckboxTreesFromNested(permissions)
            );
          }
          return;
        }

        let subscribeBrandBranches = Array.isArray(subscribeBranchesRedux)
          ? subscribeBranchesRedux
          : [];
        if (subscribeBrandBranches.length === 0 && brandId) {
          const subRes = await getSubscribeBrandInfo(token, brandId).catch(() => null);
          const subData = subRes?.data?.data ?? subRes?.data;
          if (Array.isArray(subData?.branches)) {
            subscribeBrandBranches = subData.branches;
          }
        }

        const [rolesRes, branchesRes, employeesRes] = await Promise.all([
          getRolePermissionsByBrand(token, branchId).catch(() => null),
          getBranchAndPermissionByToken(token).catch(() => null),
          GetEmployeesbyBranchID(token, branchId).catch(() => null),
        ]);
        if (cancelled) return;

        const roles =
          rolesRes?.data?.status === "success" && Array.isArray(rolesRes.data.data)
            ? rolesRes.data.data
            : [];

        const branchEntries =
          branchesRes?.data?.status === "success" &&
          Array.isArray(branchesRes.data.data)
            ? branchesRes.data.data
            : [];
        const branchEntry = branchEntries.find(
          (b) => String(b.branch_id ?? b.branchId) === String(branchId)
        );
        const branchPermissionEntry =
          resolveBranchRoleFromBranchPermissionEntry(branchEntry);

        const employees =
          employeesRes?.data?.status === "success" &&
          Array.isArray(employeesRes.data.data)
            ? employeesRes.data.data
            : [];
        const { id: selfBranchEmployeeId } = resolveCashierBranchEmployeeId(
          employeeInfo,
          employees
        );
        const selfRecord =
          selfBranchEmployeeId != null
            ? employees.find(
                (e) => Number(e.id) === Number(selfBranchEmployeeId)
              )
            : null;
        const employeeRecordBranchRole = resolveBranchRoleFromEmployeeRecord(
          selfRecord,
          branchId
        );

        const view = applyMyPermissionsView({
          roles,
          branchEntries,
          selfRecord,
          branchPermissionEntry,
          employeeRecordBranchRole,
          subscribeBrandBranches,
        });

        if (!cancelled) {
          setPermissionRoleName(view.displayRole);
          setBranchPermissionRows(view.rows);
          setPermissionTrees(view.trees);
        }
      } catch(err) {
        console.log("Error loading my permissions", err);
        if (!cancelled) {
          const view = applyMyPermissionsView({
            roles: [],
            branchEntries: [],
            selfRecord: null,
            branchPermissionEntry: null,
            employeeRecordBranchRole: null,
          });
          setPermissionRoleName(view.displayRole);
          setBranchPermissionRows(view.rows);
          setPermissionTrees(
            buildPermissionCheckboxTreesFromNested(permissions)
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void loadMyPermissions();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    token,
    branchId,
    brandId,
    branchInfo,
    employeeInfo,
    permissions,
    branchStatus,
    brandBranches,
    subscribeBranchesRedux,
  ]);

  const permissionRoleDisplay = isLoading ? "載入中…" : permissionRoleName || "—";
  const currentBranchRow = branchPermissionRows.find((row) => row.isCurrent);
  const currentBranchName =
    currentBranchRow?.branchName ||
    branchInfo?.branch_name ||
    branchInfo?.branchName ||
    (branchId != null ? `分店 #${branchId}` : "—");
  const otherBranchRows = branchPermissionRows.filter((row) => !row.isCurrent);

  const handleClose = useCallback(() => {
    setClosing(true);
    window.setTimeout(() => {
      setClosing(false);
      onClose?.();
    }, MODAL_SLIDE_CLOSE_MS);
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      if (showBranchListPopup) {
        setShowBranchListPopup(false);
        return;
      }
      handleClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, handleClose, showBranchListPopup]);

  useEffect(() => {
    if (!open) return undefined;
    const rafId = requestAnimationFrame(() => {
      permissionTreePsRef.current?.update?.();
    });
    return () => cancelAnimationFrame(rafId);
  }, [open, isLoading, permissionTrees.length]);

  if (!open && !closing) return null;

  return createPortal(
    <div className="permissionManagement my-permissions-modal-root">
      <section
        className={`EditPermissionAlert_bg${closing ? " closing" : ""}`}
        onClick={handleClose}
      >
        <div
          className="EditPermissionAlert"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="my-permissions-title"
        >
          <div className="EditPermissionAlert-header">
            <div className="EditPermissionAlert-header-left">
              <span />
            </div>
            <span id="my-permissions-title" className="fzl-24 fw-7">
              我的帳號權限
            </span>
            <div className="EditPermissionAlert-header-right useEnd">
              <button
                type="button"
                className="EditPermissionAlert-header-close"
                onClick={handleClose}
                aria-label="關閉"
              >
                <IoMdClose size={24} />
              </button>
            </div>
          </div>
          <div className="EditPermissionAlert-main">
            <div className="my-permissions-account-bar">
              <div className="my-permissions-account-bar__item">
                <span className="my-permissions-account-bar__label">名稱</span>
                <span className="my-permissions-account-bar__value">{displayName}</span>
              </div>
              <div className="my-permissions-account-bar__divider" aria-hidden />
              <div className="my-permissions-account-bar__item my-permissions-account-bar__item--branch">
                <span className="my-permissions-account-bar__label">目前分店</span>
                <span className="my-permissions-account-bar__value">
                  {isLoading ? "載入中…" : currentBranchName}
                </span>
                {branchPermissionRows.length > 0 && (
                  <button
                    type="button"
                    className="my-permissions-branches-trigger"
                    onClick={() => setShowBranchListPopup(true)}
                    disabled={isLoading}
                  >
                    查看有權限的分店
                  </button>
                )}
              </div>
              <div className="my-permissions-account-bar__divider" aria-hidden />
              <div className="my-permissions-account-bar__item">
                <span className="my-permissions-account-bar__label">本分店權限</span>
                <span className="my-permissions-account-bar__value">
                  {permissionRoleDisplay}
                </span>
              </div>
            </div>
            {/* <p className="my-permissions-tree-scope-hint fzl-12 tx-88">
              權限細項：{isLoading ? "載入中…" : currentBranchName}
            </p> */}
            <CheckoutDetailsPerfectScrollbar
              ref={permissionTreePsRef}
              className="permission_checkbox-list permission-readonly-tree-wrap scrollbar-container"
              minOverflowYPx={8}
            >
              {isLoading ? (
                <div
                  className="my-permissions-tree-loading"
                  role="status"
                  aria-live="polite"
                  aria-busy="true"
                >
                  <SelectDropdownEmptyHint
                    Icon={AiOutlineLoading3Quarters}
                    message="在載入權限中…"
                    iconSize={32}
                    spinning
                  />
                </div>
              ) : permissionTrees.length === 0 ? (
                <div className="fzl-14 tx-88">尚無權限資料</div>
              ) : (
                permissionTrees.map((tree) => (
                  <ReadOnlyPermissionCheckboxTree key={tree.label} data={tree} />
                ))
              )}
            </CheckoutDetailsPerfectScrollbar>
          </div>
          {showBranchListPopup && branchPermissionRows.length > 0 && (
            <div
              className="my-permissions-branch-popup"
              role="presentation"
              onClick={() => setShowBranchListPopup(false)}
            >
              <div
                className="my-permissions-branch-popup__panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="my-permissions-branches-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="my-permissions-branch-popup__header">
                  <h4
                    id="my-permissions-branches-title"
                    className="my-permissions-branch-popup__title"
                  >
                    分店權限名稱列表
                  </h4>
                  <button
                    type="button"
                    className="my-permissions-branch-popup__close"
                    onClick={() => setShowBranchListPopup(false)}
                    aria-label="關閉"
                  >
                    <IoMdClose size={20} />
                  </button>
                </div>
                <p className="my-permissions-branch-popup__hint fzl-12 tx-88">
                  {/* 列出品牌下所有分店；已指派者顯示權限角色名稱，未指派者顯示「無權限」。下方細項為「 */}
                  {/* {currentBranchName}」的設定。 */}
                </p>
                <ul className="my-permissions-branch-list__items">
                  {branchPermissionRows.map((row) => (
                    <li
                      key={`${row.branchId}-${row.branchName}`}
                      className={`my-permissions-branch-list__item${
                        row.isCurrent ? " is-current" : ""
                      }${row.hasPermission === false ? " is-no-permission" : ""}`}
                    >
                      <div className="my-permissions-branch-list__branch">
                        <span className="my-permissions-branch-list__branch-name">
                          {row.branchName}
                        </span>
                        {row.isCurrent && (
                          <span className="my-permissions-branch-list__badge">目前</span>
                        )}
                      </div>
                      <span className="my-permissions-branch-list__role">{row.roleName}</span>
                    </li>
                  ))}
                </ul>
                {otherBranchRows.length > 0 && (
                  <p className="my-permissions-branch-list__note fzl-12 tx-88">
                    切換右上角分店後，可檢視該分店的權限細項。
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>,
    document.body
  );
};

export default PopMyPermissions;
